import React, { useState, useEffect, useMemo } from 'react';
import { useAuth, auth } from './auth-context';
import { signOut } from 'firebase/auth';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import {
    Menu,
    X,
    ArrowLeft,
    ChartBar,
    Brain,
    Target,
    LogOut,
    TrendingUp,
    AlertCircle,
    Clock,
    CheckCircle2,
    RefreshCw,
    Crown,
    Info,
    RotateCcw
} from 'lucide-react';
import { fetchUserStats, resetAllProgress } from './firebase-utils';
import StudyTimeCard from './study-timecard';
import { computeAllDecayedMastery, getStudyRecommendations } from './lib/mastery-decay';

const ReportsDashboard = () => {
    const { user, isPaidUser } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [userStats, setUserStats] = useState({
        totalQuestions: 0,
        correctAnswers: 0,
        totalTime: 0,
        lastSession: null,
        domainMastery: {
            'Security Architecture': 0,
            'General Security Concepts': 0,
            'Security Operations': 0,
            'Threats, Vulnerabilities and Mitigations': 0,
            'Security Program Management': 0
        },
        lastPracticedByDomain: {},
        recentSessions: [],
        weakestTopics: []
    });
    const [decayData, setDecayData] = useState(null);
    const [recommendations, setRecommendations] = useState([]);
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetConfirmText, setResetConfirmText] = useState('');
    const [isResetting, setIsResetting] = useState(false);

    const handleResetProgress = async () => {
        if (resetConfirmText !== 'RESET' || !user) return;
        setIsResetting(true);
        try {
            await resetAllProgress(user.uid);
            setShowResetModal(false);
            setResetConfirmText('');
            window.location.reload();
        } catch (err) {
            console.error('Reset failed:', err);
            setIsResetting(false);
        }
    };

    useEffect(() => {
        const loadUserStats = async () => {
            if (!user) {
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const stats = await fetchUserStats(user.uid);
                const domainMastery = stats.domainMastery || {
                    'Security Architecture': 0,
                    'General Security Concepts': 0,
                    'Security Operations': 0,
                    'Threats, Vulnerabilities and Mitigations': 0,
                    'Security Program Management': 0
                };
                const lastPracticedByDomain = stats.lastPracticedByDomain || {};

                setUserStats({
                    totalQuestions: stats.totalQuestions || 0,
                    correctAnswers: stats.correctAnswers || 0,
                    totalTime: stats.totalTime || 0,
                    lastSession: stats.lastSession || null,
                    domainMastery,
                    lastPracticedByDomain,
                    recentSessions: stats.recentSessions || [],
                    weakestTopics: stats.weakestTopics || []
                });

                // Compute mastery decay for all domains
                const decayed = computeAllDecayedMastery(domainMastery, lastPracticedByDomain);
                setDecayData(decayed);
                setRecommendations(getStudyRecommendations(decayed));
            } catch (error) {
                console.error('Error loading user stats:', error);
                setError('Failed to load user data. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        loadUserStats();
    }, [user]);

    const domainColors = {
        'Security Architecture': '#38b0e8',
        'General Security Concepts': '#FF6B6B',
        'Security Operations': '#d68bc6',
        'Threats, Vulnerabilities and Mitigations': '#4ECDC4',
        'Security Program Management': '#FFEEAD'
    };

    const overallPct = useMemo(() => {
        const pct = (userStats.correctAnswers / userStats.totalQuestions) * 100;
        if (!Number.isFinite(pct)) return 0;
        return Math.max(0, Math.min(100, pct));
    }, [userStats.correctAnswers, userStats.totalQuestions]);

    const overallPieData = useMemo(
        () => [
            { name: 'Correct', value: overallPct },
            { name: 'Remaining', value: 100 - overallPct },
        ],
        [overallPct]
    );

    const domains = Object.entries(userStats.domainMastery).map(([name, rawMastery]) => {
        const decay = decayData?.[name];
        return {
            name,
            value: (rawMastery || 0) * 100,
            color: domainColors[name] || '#38b0e8',
            progress: decay ? decay.decayed : (rawMastery || 0),
            rawProgress: rawMastery || 0,
            decayPercent: decay ? decay.decayPercent : 0,
            daysSince: decay ? decay.daysSince : null
        };
    });

    // Weekly readiness — resets every 7 days, based on sessions from the current week
    const { overallReadiness, weeklyStats } = useMemo(() => {
        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const weeklySessions = (userStats.recentSessions || []).filter(s => {
            if (!s.date) return false;
            return new Date(s.date) >= sevenDaysAgo;
        });

        const weekCorrect = weeklySessions.reduce((sum, s) => sum + (s.questionsAnswered * s.score / 100), 0);
        const weekTotal = weeklySessions.reduce((sum, s) => sum + s.questionsAnswered, 0);
        const weeklyPct = weekTotal > 0 ? (weekCorrect / weekTotal) * 100 : 0;

        return {
            overallReadiness: Math.min(100, Math.max(0, weeklyPct)),
            weeklyStats: { correct: Math.round(weekCorrect), total: weekTotal, sessions: weeklySessions.length }
        };
    }, [userStats.recentSessions]);

    const readinessLabel = useMemo(() => {
        if (overallReadiness >= 90) return { text: 'Strong Pass', color: 'text-green-400', bg: 'bg-green-500/15 ring-green-500/30' };
        if (overallReadiness >= 85) return { text: 'Pass', color: 'text-emerald-400', bg: 'bg-emerald-500/15 ring-emerald-500/30' };
        if (overallReadiness >= 78) return { text: 'Borderline', color: 'text-yellow-400', bg: 'bg-yellow-500/15 ring-yellow-500/30' };
        if (overallReadiness >= 73) return { text: 'Almost', color: 'text-orange-400', bg: 'bg-orange-500/15 ring-orange-500/30' };
        return { text: 'Needs Improvement', color: 'text-red-400', bg: 'bg-red-500/15 ring-red-500/30' };
    }, [overallReadiness]);

    const [showReadinessTooltip, setShowReadinessTooltip] = useState(false);
    const [showBadgeTooltip, setShowBadgeTooltip] = useState(false);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#070B14] text-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
                    <p className="mt-4">Loading your progress...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#070B14] text-gray-100 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto p-6 rounded-2xl bg-white/5 ring-1 ring-white/10">
                    <div className="text-red-400 text-xl mb-4">⚠️</div>
                    <p className="text-gray-300 mb-4">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#070B14] text-gray-100">
            {/* ambient glow */}
            <div className="pointer-events-none fixed inset-0 overflow-hidden">
                <div className="absolute -top-40 left-1/3 h-[520px] w-[820px] rounded-full bg-cyan-500/10 blur-3xl" />
                <div className="absolute bottom-[-220px] right-[-220px] h-[560px] w-[560px] rounded-full bg-blue-500/10 blur-3xl" />
            </div>

            {/* Hamburger — only visible when sidebar is closed on mobile */}
            {!isSidebarOpen && (
                <button
                    onClick={() => setSidebarOpen(true)}
                    className="fixed top-4 left-4 z-50 md:hidden rounded-xl bg-white/10 p-2.5 text-white ring-1 ring-white/10 backdrop-blur"
                    aria-label="Open menu"
                >
                    <Menu size={24} />
                </button>
            )}

            {/* Sidebar Navigation */}
            <nav
                className={`fixed top-0 left-0 z-40 h-screen w-full md:w-64 overflow-y-auto md:overflow-hidden md:flex md:flex-col border-r border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-2xl ring-1 ring-inset ring-white/[0.08] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_40px_-10px_rgba(6,182,212,0.08)] transition-transform duration-200 ease-in-out ${isSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
            >
                <div className="p-6 border-b border-white/10 flex items-start justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-blue-400 mb-2">Security+ Crammer</h1>
                        <div className="flex items-center text-sm text-white/70">
                            Progress Overview
                        </div>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="md:hidden rounded-xl bg-white/10 p-2 text-white ring-1 ring-white/10 flex-shrink-0"
                        aria-label="Close menu"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="md:flex-grow md:overflow-y-auto p-4">
                    <ul className="space-y-3">
                        <li>
                            <Link to="/reports" className="flex items-center px-3 py-2 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
                                <ChartBar size={18} className="mr-3" />
                                <span className="monospace">Reports</span>
                            </Link>
                        </li>
                        <li>
                            <Link to="/practice" className="flex items-center px-3 py-2 rounded-lg text-gray-300 hover:bg-white/10 transition-colors duration-200">
                                <Target size={18} className="mr-3" />
                                <span className="monospace">Practice</span>
                            </Link>
                        </li>
                        <li>
                            <Link to="/flashcards" className="flex items-center px-3 py-2 rounded-lg text-gray-300 hover:bg-white/10 transition-colors duration-200">
                                <Brain size={18} className="mr-3" />
                                <span className="monospace">Flashcards</span>
                            </Link>
                        </li>
                    </ul>
                </div>

                {/* Bottom Navigation */}
                <div className="p-4 px-6 md:px-4 pb-10 md:pb-4 md:border-t border-white/10 space-y-3">
                    {!isPaidUser ? (
                        <Link to="/pricing" className="block">
                            <div className="w-full rounded-xl bg-gradient-to-r from-cyan-600/10 to-blue-600/10 ring-1 ring-cyan-500/30 hover:ring-cyan-500/50 px-3 py-2 transition-colors group flex items-center justify-center">
                                <Crown size={14} className="text-cyan-400 mr-2" />
                                <span className="text-xs font-semibold text-cyan-400">Upgrade</span>
                                <span className="text-xs text-white/45 ml-1.5">· Unlock all modes</span>
                            </div>
                        </Link>
                    ) : (
                        <Link to="/pricing" className="block">
                            <div className="w-full rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-white/10 px-3 py-2 text-white/55 hover:text-white/75 flex items-center justify-center text-xs transition-colors">
                                <Crown size={14} className="mr-2 text-cyan-400" />
                                Manage Subscription
                            </div>
                        </Link>
                    )}
                    <Link to="/" className="block">
                        <button className="w-full rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 px-3 py-3 text-white flex items-center justify-center">
                            <ArrowLeft size={18} className="mr-3" />
                            <span className="text-sm">Back to Overview</span>
                        </button>
                    </Link>
                    <button onClick={() => signOut(auth)} className="w-full rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-red-500/30 px-3 py-3 text-red-400 flex items-center justify-center text-sm">
                        <LogOut size={18} className="mr-3" />
                        Sign Out
                    </button>
                    <button onClick={() => setShowResetModal(true)} className="w-full rounded-xl hover:bg-white/5 px-3 py-2 text-white/30 hover:text-white/50 flex items-center justify-center text-xs transition-colors">
                        <RotateCcw size={14} className="mr-2" />
                        Reset All Progress
                    </button>
                </div>
            </nav>

            {/* Reset Progress Confirmation Modal */}
            {showResetModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="rounded-2xl bg-[#0B1224] ring-1 ring-white/10 p-6 max-w-sm w-full shadow-2xl">
                        <h3 className="text-lg font-semibold text-white mb-2">Reset All Progress?</h3>
                        <p className="text-sm text-white/55 mb-1">
                            This will permanently erase all your progress including:
                        </p>
                        <ul className="text-sm text-white/55 mb-4 list-disc list-inside space-y-1">
                            <li>Domain mastery scores</li>
                            <li>Session history</li>
                            <li>Spaced repetition data</li>
                            <li>All practice statistics</li>
                        </ul>
                        <p className="text-sm text-red-400/80 font-medium mb-4">
                            This cannot be undone. Type <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded">RESET</span> to confirm.
                        </p>
                        <input
                            type="text"
                            value={resetConfirmText}
                            onChange={(e) => setResetConfirmText(e.target.value)}
                            placeholder="Type RESET to confirm"
                            className="w-full rounded-xl bg-white/5 ring-1 ring-white/10 px-4 py-2.5 text-sm text-white placeholder-white/30 mb-4 outline-none focus:ring-red-500/50"
                        />
                        <div className="flex gap-3">
                            <button
                                onClick={() => { setShowResetModal(false); setResetConfirmText(''); }}
                                className="flex-1 rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-white/10 px-4 py-2.5 text-sm text-white/70"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleResetProgress}
                                disabled={resetConfirmText !== 'RESET' || isResetting}
                                className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                                    resetConfirmText === 'RESET' && !isResetting
                                        ? 'bg-red-600 hover:bg-red-500 text-white'
                                        : 'bg-white/5 text-white/30 cursor-not-allowed'
                                }`}
                            >
                                {isResetting ? 'Resetting...' : 'Erase Everything'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <main className="relative md:ml-64 p-4 md:p-8">
                <div className="max-w-6xl mx-auto space-y-6">
                    {/* Overall Readiness */}
                    <div className={`rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur ${(showReadinessTooltip || showBadgeTooltip) ? 'relative z-20' : ''}`}>
                        <div className="flex items-center justify-between mb-3">
                            <div className="relative">
                                <div
                                    className="flex items-center gap-2 text-sm text-white/75 cursor-help"
                                    onMouseEnter={() => setShowReadinessTooltip(true)}
                                    onMouseLeave={() => setShowReadinessTooltip(false)}
                                    onClick={() => setShowReadinessTooltip(!showReadinessTooltip)}
                                >
                                    <TrendingUp size={18} className="text-cyan-300" />
                                    Overall Readiness
                                    <Info size={14} className="text-white/40" />
                                </div>
                                {showReadinessTooltip && (
                                    <div className="fixed left-4 right-4 md:absolute md:left-0 md:right-auto top-auto md:top-7 z-[100] md:w-[26rem] rounded-xl bg-[#0B1224] ring-1 ring-white/10 p-4 shadow-2xl text-xs md:text-sm leading-relaxed text-white/65 mt-2 md:mt-0">
                                        <p className="mb-2">This score resets every 7 days. It's based on your last week of practice ({weeklyStats.total} question{weeklyStats.total !== 1 ? 's' : ''} across {weeklyStats.sessions} session{weeklyStats.sessions !== 1 ? 's' : ''}).</p>
                                        <p>Practice consistently each week to see your readiness improve.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="text-4xl font-bold">{overallReadiness.toFixed(1)}%</div>
                            <div className="relative">
                                <span
                                    onMouseEnter={() => setShowBadgeTooltip(true)}
                                    onMouseLeave={() => setShowBadgeTooltip(false)}
                                    onClick={() => setShowBadgeTooltip(!showBadgeTooltip)}
                                    className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full ring-1 cursor-help ${readinessLabel.bg} ${readinessLabel.color}`}
                                >
                                    {readinessLabel.text}
                                    <Info size={13} className="opacity-60" />
                                </span>
                                {showBadgeTooltip && (
                                    <div className="fixed left-4 right-4 md:absolute md:left-0 md:right-auto top-auto md:top-9 z-[100] md:w-[26rem] rounded-xl bg-[#0B1224] ring-1 ring-white/10 p-4 shadow-2xl text-xs md:text-sm leading-relaxed text-white/65 mt-2 md:mt-0">
                                        <p>The CompTIA Security+ (SY0-701) passing score is 750/900, generally requiring ~83% correct answers. This is not a predictor of actual exam results.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-white/10">
                            <div
                                className="h-full rounded-full transition-all duration-700"
                                style={{
                                    width: `${overallReadiness}%`,
                                    background: overallReadiness >= 78
                                        ? 'linear-gradient(90deg, #22d3ee, #3b82f6)'
                                        : 'linear-gradient(90deg, #ef4444, #f59e0b)'
                                }}
                            />
                        </div>
                    </div>

                    {/* Stats Overview */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                        <div className="relative overflow-hidden rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-semibold text-white monospace">Overall Progress</h3>
                                <Target size={20} className="text-cyan-300" />
                            </div>
                            <div className="mt-3 flex items-center gap-4">
                                <div className="h-16 w-16">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={overallPieData}
                                                dataKey="value"
                                                innerRadius={22}
                                                outerRadius={30}
                                                paddingAngle={2}
                                                stroke="transparent"
                                            >
                                                <Cell fill="#22d3ee" />
                                                <Cell fill="rgba(255,255,255,0.10)" />
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div>
                                    <div className="text-3xl font-bold">{overallPct.toFixed(1)}%</div>
                                    <div className="text-sm text-white/70">
                                        {userStats.correctAnswers} of {userStats.totalQuestions} correct
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-semibold text-white monospace">Questions</h3>
                                <CheckCircle2 size={20} className="text-cyan-300" />
                            </div>
                            <div className="text-3xl font-bold">{userStats.totalQuestions}</div>
                            <div className="text-sm text-white/70">Total attempted</div>
                        </div>

                        <StudyTimeCard userStats={userStats} />

                        <div className="relative overflow-hidden rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-semibold text-white monospace">Last Session</h3>
                                <TrendingUp size={20} className="text-cyan-300" />
                            </div>
                            <div className="text-3xl font-bold">
                                {userStats.lastSession?.score.toFixed(1) || 0}%
                            </div>
                            <div className="text-sm text-white/70">
                                {userStats.lastSession?.date ? new Date(userStats.lastSession.date).toLocaleDateString() : 'No sessions yet'}
                            </div>
                        </div>
                    </div>

                    {/* Domain Progress and Recommendations */}
                    <div className="grid gap-6 md:grid-cols-2">
                        {/* Domain Progress with Decay Visualization */}
                        <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur">
                            <h3 className="mb-6 text-lg font-semibold text-white monospace">Domain Mastery</h3>
                            <div className="space-y-5">
                                {domains.map((domain, index) => {
                                    const pct = domain.progress * 100;
                                    const rawPct = domain.rawProgress * 100;
                                    const isPassing = pct >= 78;
                                    const barGradient = isPassing
                                        ? 'linear-gradient(90deg, #22d3ee, #3b82f6)'
                                        : 'linear-gradient(90deg, #ef4444, #f59e0b)';
                                    const peakGradient = isPassing
                                        ? 'linear-gradient(90deg, rgba(34,211,238,0.25), rgba(59,130,246,0.25))'
                                        : 'linear-gradient(90deg, rgba(239,68,68,0.25), rgba(245,158,11,0.25))';
                                    return (
                                        <div key={index}>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <span className="text-sm text-white/75">{domain.name}</span>
                                                <div className="flex items-center space-x-2">
                                                    <span className={`text-sm font-medium ${isPassing ? 'text-white' : 'text-yellow-400'}`}>
                                                        {pct.toFixed(1)}%
                                                    </span>
                                                    {domain.decayPercent > 5 && (
                                                        <span className="text-xs text-yellow-400" title={`${Math.round(domain.decayPercent)}% lost since last practice`}>
                                                            ({Math.round(domain.decayPercent)}% faded)
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {/* Gradient bar with peak ghost */}
                                            <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-white/10">
                                                {/* Raw mastery (peak) — faded gradient */}
                                                <div
                                                    className="absolute top-0 left-0 h-full rounded-full transition-all duration-500"
                                                    style={{
                                                        width: `${Math.min(100, Math.max(0, rawPct))}%`,
                                                        background: peakGradient
                                                    }}
                                                ></div>
                                                {/* Decayed mastery (current) — solid gradient */}
                                                <div
                                                    className="absolute top-0 left-0 h-full rounded-full transition-all duration-500"
                                                    style={{
                                                        width: `${Math.min(100, Math.max(0, pct))}%`,
                                                        background: barGradient
                                                    }}
                                                ></div>
                                            </div>
                                            {domain.daysSince !== null && (
                                                <div className="mt-1 text-xs text-white/55">
                                                    Last practiced: {domain.daysSince === 0 ? 'today' : `${domain.daysSince} day${domain.daysSince !== 1 ? 's' : ''} ago`}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="mt-4 flex items-center text-xs text-white/55">
                                <div className="w-4 h-2 rounded-full mr-1.5" style={{ background: 'linear-gradient(90deg, #22d3ee, #3b82f6)' }}></div>
                                <span className="mr-4">Passing</span>
                                <div className="w-4 h-2 rounded-full mr-1.5" style={{ background: 'linear-gradient(90deg, #ef4444, #f59e0b)' }}></div>
                                <span>Needs Work</span>
                            </div>
                        </div>

                        {/* Decay-Aware Recommendations */}
                        <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur">
                            <h3 className="mb-4 text-lg font-semibold text-white monospace">Study Recommendations</h3>
                            <div className="space-y-4">
                                {recommendations.length > 0 ? (
                                    recommendations.slice(0, 4).map((rec, index) => (
                                        <div key={index} className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10 transition-all duration-300 hover:-translate-y-[3px]">
                                            <div className="flex items-start">
                                                {rec.urgency === 'high' ? (
                                                    <RefreshCw className="text-red-400 flex-shrink-0 mt-1" size={20} />
                                                ) : rec.urgency === 'medium' ? (
                                                    <AlertCircle className="text-yellow-500 flex-shrink-0 mt-1" size={20} />
                                                ) : (
                                                    <Target className="text-blue-400 flex-shrink-0 mt-1" size={20} />
                                                )}
                                                <div className="ml-3">
                                                    <div className="flex items-center space-x-2">
                                                        <h4 className="font-medium">{rec.domain}</h4>
                                                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                            rec.urgency === 'high' ? 'bg-red-900/50 text-red-300' :
                                                            rec.urgency === 'medium' ? 'bg-yellow-900/50 text-yellow-300' :
                                                            'bg-blue-900/50 text-blue-300'
                                                        }`}>
                                                            {rec.urgency === 'high' ? 'Review Now' :
                                                             rec.urgency === 'medium' ? 'Review Soon' : 'New Topic'}
                                                        </span>
                                                    </div>
                                                    <p className="mt-1 text-sm text-white/70">
                                                        {rec.message}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    domains
                                        .sort((a, b) => a.progress - b.progress)
                                        .slice(0, 3)
                                        .map((domain, index) => (
                                            <div key={index} className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10 transition-all duration-300 hover:-translate-y-[3px]">
                                                <div className="flex items-start">
                                                    <AlertCircle className="text-yellow-500 flex-shrink-0 mt-1" size={20} />
                                                    <div className="ml-3">
                                                        <h4 className="font-medium">{domain.name}</h4>
                                                        <p className="mt-1 text-sm text-white/70">
                                                            Current mastery: {(domain.progress * 100).toFixed(1)}%
                                                        </p>
                                                        <p className="mt-1 text-sm text-white/70">
                                                            Focus on this domain to improve your overall score
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                )}

                                <Link
                                    to="/practice"
                                    className="block mt-6 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 px-4 py-2 text-white text-center"
                                >
                                    Start Practice Session
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Recent Sessions */}
                    <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-white monospace">Recent Sessions</h3>
                            {userStats.recentSessions.length > 3 && (
                                <Link to="/sessions" className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                                    View All ({userStats.recentSessions.length})
                                </Link>
                            )}
                        </div>
                        <div className="space-y-4">
                            {[...userStats.recentSessions].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 3).map((session, index) => (
                                <div key={index} className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10 transition-all duration-300 hover:-translate-y-[3px]">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="font-medium">
                                                {session.mode.charAt(0).toUpperCase() + session.mode.slice(1)} Session
                                            </div>
                                            <div className="text-sm text-white/70">
                                                {session.questionsAnswered} questions, {session.score.toFixed(1)}% accuracy
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-sm text-white/70">
                                                {new Date(session.date).toLocaleDateString()}
                                            </div>
                                            <div className="text-sm text-white/70">
                                                {Math.floor(session.duration / 60)}m {session.duration % 60}s
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {userStats.recentSessions.length === 0 && (
                                <p className="text-white/70 text-sm">No sessions yet. Start practicing to see your history here.</p>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ReportsDashboard;